import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { dateToMinutes, formatMinutesAsTime, isAfter, isBefore, isSameDay, MINUTES_IN_DAY, MINUTES_IN_HOUR, startOfDay, startOfToday, } from '@repo/date-time';
import classNames from 'classnames';
import Button from '../Button/Button';
import { FAR_FUTURE_YEARS, PRESET_AFTERNOON_MINUTES, PRESET_END_OF_DAY_MINUTES, PRESET_MIDNIGHT_MINUTES, PRESET_MORNING_MINUTES, } from '../calendarCore/constants';
import DayScale from '../InertiaSlider/DayScale';
import InertiaSlider from '../InertiaSlider/InertiaSlider';
import MonthCalendarNavigator from '../MonthCalendar/MonthCalendarNavigator';
import { getLabels } from '../labels';
import { DEFAULT_MINUTE_STEP, TIME_SLIDER_RATIO } from './constants';
const initialDraftTimeMinutes = (value) => {
    if (value !== undefined)
        return dateToMinutes(value);
    return dateToMinutes(new Date());
};
const initialDraftDate = (value, mode) => {
    if (value !== undefined)
        return startOfDay(value);
    if (mode === 'time')
        return startOfToday();
    return undefined;
};
const combineDraft = (draftDate, draftTimeMinutes) => {
    const combined = new Date(draftDate);
    const hours = Math.floor(draftTimeMinutes / MINUTES_IN_HOUR);
    const minutes = draftTimeMinutes % MINUTES_IN_HOUR;
    combined.setHours(hours, minutes, 0, 0);
    return combined;
};
const isWithinBounds = (candidate, min, max) => {
    if (min !== undefined && isBefore(candidate, min))
        return false;
    if (max !== undefined && isAfter(candidate, max))
        return false;
    return true;
};
export default function InstantInputDialogContents({ testId, mode, open, value, onChange, min, max, disablePast = false, minDate, minuteStep, }) {
    const calendarLabels = getLabels().calendar;
    const step = minuteStep ?? DEFAULT_MINUTE_STEP;
    const farFutureMaxDate = useMemo(() => {
        const today = startOfToday();
        return new Date(today.getFullYear() + FAR_FUTURE_YEARS, today.getMonth(), today.getDate());
    }, []);
    const calendarMaxDate = max ?? farFutureMaxDate;
    const [draftDate, setDraftDate] = useState(() => initialDraftDate(value, mode));
    const [draftTimeMinutes, setDraftTimeMinutes] = useState(() => initialDraftTimeMinutes(value));
    const [openNow, setOpenNow] = useState(() => new Date());
    const prevOpenRef = useRef(open);
    useEffect(() => {
        if (open && !prevOpenRef.current) {
            setOpenNow(new Date());
            setDraftDate(initialDraftDate(value, mode));
            setDraftTimeMinutes(initialDraftTimeMinutes(value));
        }
        prevOpenRef.current = open;
    }, [open, value, mode]);
    const showCalendar = mode === 'date' || mode === 'datetime';
    const showTime = mode === 'time' || mode === 'datetime';
    const disablePastFloor = mode === 'date' ? startOfDay(openNow) : openNow;
    const effectiveMin = disablePast && (min === undefined || isAfter(disablePastFloor, min)) ? disablePastFloor : min;
    const tryBuildCommit = () => {
        if (mode === 'time') {
            const base = value !== undefined ? startOfDay(value) : startOfToday();
            return combineDraft(base, draftTimeMinutes);
        }
        if (draftDate === undefined)
            return undefined;
        if (mode === 'date') {
            return startOfDay(draftDate);
        }
        return combineDraft(draftDate, draftTimeMinutes);
    };
    const candidateCommit = tryBuildCommit();
    const canCommit = candidateCommit !== undefined && isWithinBounds(candidateCommit, effectiveMin, max);
    const handleDateChosen = (date) => {
        const nextDraftDate = startOfDay(date);
        setDraftDate(nextDraftDate);
        if (mode === 'date' && isWithinBounds(nextDraftDate, effectiveMin, max)) {
            onChange(nextDraftDate);
        }
    };
    const handleTimeChange = (raw) => {
        const snapped = Math.round(raw / step) * step;
        setDraftTimeMinutes(snapped);
    };
    const handleSetMidnight = () => setDraftTimeMinutes(PRESET_MIDNIGHT_MINUTES);
    const handleSetMorning = () => setDraftTimeMinutes(PRESET_MORNING_MINUTES);
    const handleSetAfternoon = () => setDraftTimeMinutes(PRESET_AFTERNOON_MINUTES);
    const handleSetEndOfDay = () => setDraftTimeMinutes(PRESET_END_OF_DAY_MINUTES);
    const handleConfirm = () => {
        if (candidateCommit !== undefined && canCommit)
            onChange(candidateCommit);
    };
    const timeMinimum = mode === 'time' && effectiveMin !== undefined
        ? dateToMinutes(effectiveMin)
        : mode === 'datetime' &&
            draftDate !== undefined &&
            effectiveMin !== undefined &&
            isSameDay(draftDate, effectiveMin)
            ? dateToMinutes(effectiveMin)
            : 0;
    const timeMaximum = mode === 'time' && max !== undefined
        ? dateToMinutes(max)
        : mode === 'datetime' && draftDate !== undefined && max !== undefined && isSameDay(draftDate, max)
            ? dateToMinutes(max)
            : MINUTES_IN_DAY;
    const isPresetDisabled = (presetMinutes) => presetMinutes < timeMinimum || presetMinutes > timeMaximum;
    return (_jsxs("div", { className: "flex flex-col", children: [showCalendar && (_jsx("div", { className: classNames('border-stroke-base mt-4 flex flex-col gap-3 border-t px-4 pt-4', !showTime && 'mb-4 border-b'), children: _jsx(MonthCalendarNavigator, { initialDate: draftDate ?? value ?? min ?? startOfToday(), disablePast: disablePast, minDate: minDate ?? min, maxDate: calendarMaxDate, flaggedDates: [], selectionStart: draftDate, testId: testId.append('calendar'), onDateChosen: handleDateChosen }) })), showTime && (_jsxs("div", { className: "border-stroke-base mt-4 mb-4 flex w-full items-start gap-5 border-t border-b px-4 pb-2", children: [_jsx("div", { className: "text-heading shrink-0 pt-8 text-sm font-semibold tabular-nums", "data-testid": testId.append('time').append('value'), children: formatMinutesAsTime(draftTimeMinutes) }), _jsxs("div", { className: "mb-2 flex flex-1 flex-col", children: [_jsx("div", { className: "h-20 w-full", children: _jsx(InertiaSlider, { value: draftTimeMinutes, min: timeMinimum, max: timeMaximum, ratio: TIME_SLIDER_RATIO, scale: _jsx(DayScale, {}), onChange: handleTimeChange }) }), _jsxs("div", { className: "flex gap-2", children: [_jsx(Button, { testId: testId.append('preset').append('0'), color: "tertiary", size: "xs", label: "0:00", disabled: isPresetDisabled(PRESET_MIDNIGHT_MINUTES), onClick: handleSetMidnight }), _jsx(Button, { testId: testId.append('preset').append('8'), color: "tertiary", size: "xs", label: "8:00", disabled: isPresetDisabled(PRESET_MORNING_MINUTES), onClick: handleSetMorning }), _jsx(Button, { testId: testId.append('preset').append('16'), color: "tertiary", size: "xs", label: "16:00", disabled: isPresetDisabled(PRESET_AFTERNOON_MINUTES), onClick: handleSetAfternoon }), _jsx(Button, { testId: testId.append('preset').append('24'), color: "tertiary", size: "xs", label: "24:00", disabled: isPresetDisabled(PRESET_END_OF_DAY_MINUTES), onClick: handleSetEndOfDay })] })] })] })), _jsx("div", { className: "bg-primary flex justify-end px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))]", children: _jsx(Button, { testId: testId.append('confirm'), color: "brand", size: "base", label: calendarLabels.confirm, disabled: !canCommit, onClick: handleConfirm }) })] }));
}
